// pages/HandKeyboard.jsx
import React, { useState, useEffect, useRef } from "react";
import Webcam from "react-webcam";
import * as handpose from "@tensorflow-models/handpose";
import "@tensorflow/tfjs-backend-webgl";

const keyColors = {
  esc: "from-[#FFE5B4] to-[#E89B61]",
  f: "from-[#D0E1FF] to-[#7DA7FF]",
  number: "from-[#FFF7B4] to-[#FFE561]",
  del: "from-[#FFD1B4] to-[#FF8B61]",
  control: "from-[#D6B4FF] to-[#A261E8]",
  special: "from-[#FFE5B4] to-[#E89A61]",
  default: "from-[#E0E0E0] to-[#A0A0A0]",
};

const activeColors = {
  esc: "from-[#E89B61] to-[#C97A3D]",
  f: "from-[#7DA7FF] to-[#3D6FE8]",
  number: "from-[#FFE561] to-[#D4B200]",
  del: "from-[#FF8B61] to-[#C95A3A]",
  control: "from-[#A261E8] to-[#7035B6]",
  special: "from-[#E89B61] to-[#C97A3D]",
  default: "from-[#A0A0A0] to-[#707070]",
};

const depthColors = {
  esc: "#E89B61",
  f: "#7DA7FF",
  number: "#FFE561",
  del: "#FF8B61",
  control: "#A261E8",
  special: "#E89B61",
  default: "#A0A0A0",
};

const KeyLabel = ({ label }) => {
  if (label === "Caps Lock") return <span className="text-sm font-semibold whitespace-nowrap">{label}</span>;
  if (label.includes(" ")) {
    const [primary, secondary] = label.split(" ");
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <span className="absolute top-1 left-1 text-xs">{secondary}</span>
        <span className="text-base font-bold">{primary}</span>
      </div>
    );
  }
  return <span className="text-sm font-semibold">{label}</span>;
};

export default function HandKeyboard() {
  const webcamRef = useRef(null);
  const wrapperRef = useRef(null);
  const buttonRefs = useRef({});
  const lastPress = useRef({});
  const modelRef = useRef(null);
  const rafRef = useRef(null);

  const [camOn, setCamOn] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [activeUid, setActiveUid] = useState(null);

  const PRESS_COOLDOWN = 0.18 * 1000;
  const PINCH_THRESH = 40;
  const MIRRORED = true;

  const rows = [
    [
      { label: "Esc", type: "esc" },
      { label: "F1", type: "f" }, { label: "F2", type: "f" }, { label: "F3", type: "f" }, { label: "F4", type: "f" },
      { label: "F5", type: "f" }, { label: "F6", type: "f" }, { label: "F7", type: "f" }, { label: "F8", type: "f" },
      { label: "F9", type: "f" }, { label: "F10", type: "f" }, { label: "F11", type: "f" }, { label: "F12", type: "f" },
      { label: "Del", type: "del" },
    ],
    [
      { label: "` ~", type: "default" }, { label: "1 !", type: "number" }, { label: "2 @", type: "number" },
      { label: "3 #", type: "number" }, { label: "4 $", type: "number" }, { label: "5 %", type: "number" },
      { label: "6 ^", type: "number" }, { label: "7 &", type: "number" }, { label: "8 *", type: "number" },
      { label: "9 (", type: "number" }, { label: "0 )", type: "number" }, { label: "- _", type: "default" },
      { label: "= +", type: "default" }, { label: "Backspace", type: "special", wide: "w-[100px]" },
    ],
    [
      { label: "Tab", type: "control", wide: "w-[80px]" }, { label: "Q", type: "default" }, { label: "W", type: "default" },
      { label: "E", type: "default" }, { label: "R", type: "default" }, { label: "T", type: "default" },
      { label: "Y", type: "default" }, { label: "U", type: "default" }, { label: "I", type: "default" },
      { label: "O", type: "default" }, { label: "P", type: "default" }, { label: "[ {", type: "default" },
      { label: "] }", type: "default" }, { label: "\\ |", type: "default", wide: "w-[80px]" },
    ],
    [
      { label: "Caps Lock", type: "control", wide: "w-[90px]" }, { label: "A", type: "default" }, { label: "S", type: "default" },
      { label: "D", type: "default" }, { label: "F", type: "default" }, { label: "G", type: "default" },
      { label: "H", type: "default" }, { label: "J", type: "default" }, { label: "K", type: "default" },
      { label: "L", type: "default" }, { label: "; :", type: "default" }, { label: "' \"", type: "default" },
      { label: "Enter", type: "control", wide: "w-[120px]" },
    ],
    [
      { label: "Shift", type: "control", wide: "w-[120px]" }, { label: "Z", type: "default" }, { label: "X", type: "default" },
      { label: "C", type: "default" }, { label: "V", type: "default" }, { label: "B", type: "default" },
      { label: "N", type: "default" }, { label: "M", type: "default" }, { label: ", <", type: "default" },
      { label: ". >", type: "default" }, { label: "/ ?", type: "default" }, { label: "Shift", type: "control", wide: "w-[140px]" },
    ],
    [
      { label: "Ctrl", type: "control" }, { label: "Fn", type: "control" }, { label: "Win", type: "control" },
      { label: "Alt", type: "control" }, { label: "Space", type: "special", wide: "w-[400px]" },
      { label: "Alt", type: "control" }, { label: "Menu", type: "control" }, { label: "Ctrl", type: "control" },
      { label: "←", type: "special" }, { label: "↓", type: "special" }, { label: "↑", type: "special" }, { label: "→", type: "special" },
    ],
  ];

  const parseMain = (label) => (label.includes(" ") && label.length > 2 ? label.split(" ")[0] : label);

  const applyKeyAction = (label) => {
    const main = parseMain(label);
    if (main === "Backspace" || main === "Del") setTypedText((p) => p.slice(0, -1));
    else if (main === "Space") setTypedText((p) => p + " ");
    else if (main === "Enter") setTypedText((p) => p + "\n");
    else if (main === "Tab") setTypedText((p) => p + "\t");
    else if (main.length === 1) setTypedText((p) => p + main);
  };

  const handlePress = (uid, label) => {
    const now = Date.now();
    if (now - (lastPress.current[uid] || 0) < PRESS_COOLDOWN) return;
    lastPress.current[uid] = now;
    setActiveUid(uid);
    applyKeyAction(label);
    setTimeout(() => setActiveUid((curr) => (curr === uid ? null : curr)), Math.max(120, PRESS_COOLDOWN / 2));
  };

  const detectAndHandle = async (model) => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState !== 4) return;

    let preds = [];
    try { preds = await model.estimateHands(video); } catch { return; }
    if (!preds.length) return;

    const hand = preds[0];
    const [ix, iy] = hand.landmarks[8];
    const [mx, my] = hand.landmarks[12];
    const pinchDist = Math.hypot(ix - mx, iy - my);

    const vRect = video.getBoundingClientRect();
    const vW = video.videoWidth || video.width;
    const vH = video.videoHeight || video.height;
    const normX = MIRRORED ? 1 - ix / vW : ix / vW;
    const normY = iy / vH;
    const scaledX = vRect.left + normX * vRect.width;
    const scaledY = vRect.top + normY * vRect.height;

    let hitUid = null;
    for (const uid of Object.keys(buttonRefs.current)) {
      const info = buttonRefs.current[uid];
      if (!info?.el) continue;
      const r = info.el.getBoundingClientRect();
      if (scaledX >= r.left && scaledX <= r.right && scaledY >= r.top && scaledY <= r.bottom) {
        hitUid = uid;
        break;
      }
    }

    const scaleFactor = vW / 640;
    const scaledThresh = PINCH_THRESH * scaleFactor;
    if (hitUid && pinchDist < scaledThresh) handlePress(hitUid, buttonRefs.current[hitUid].label);

    const marker = document.getElementById("hk-finger-dot");
    if (marker && wrapperRef.current) {
      const wrapRect = wrapperRef.current.getBoundingClientRect();
      marker.style.left = `${scaledX - wrapRect.left}px`;
      marker.style.top = `${scaledY - wrapRect.top}px`;
      marker.style.opacity = "1";
    }
  };

  useEffect(() => {
    if (!camOn) return;
    let canceled = false;
    const loadModel = async () => {
      if (!modelRef.current) modelRef.current = await handpose.load();
      const loop = async () => {
        if (canceled || !camOn) return;
        await detectAndHandle(modelRef.current);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    };
    loadModel();
    return () => { canceled = true; if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [camOn]);

  return (
    <div className="flex flex-col items-center gap-6 mt-16 px-4 md:px-16">
      {/* Intro Section */}
      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center drop-shadow-md">
        Virtual Hand Tracking Keyboard
      </h1>
      <p className="text-center max-w-3xl text-gray-600 text-lg md:text-xl">
        Experience a professional-grade virtual keyboard controlled entirely by hand gestures. Perfect for hands-free typing, presentations, and accessibility applications.
      </p>
      <p className="text-center max-w-3xl text-gray-500 text-md md:text-lg">
        Use the preview below to see your webcam and start interacting with the keyboard in real-time.
      </p>

      {/* Start/Stop Preview Button */}
      <button
        onClick={() => setCamOn((prev) => !prev)}
        className={`px-6 py-3 rounded-lg text-white font-semibold shadow-lg hover:shadow-xl transition-colors ${
          camOn ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
        }`}
      >
        {camOn ? "Stop Preview" : "Start Preview"}
      </button>

      {/* Textarea & Clear button always visible */}
      <div className="flex flex-col md:flex-row gap-4 mt-4 items-center w-full max-w-[1024px]">
        <textarea
          value={typedText}
          onChange={(e) => setTypedText(e.target.value)}
          placeholder="Keys will appear here..."
          className="flex-1 p-3 border rounded-lg shadow-md resize-none text-black h-28"
        />
        <button
          onClick={() => setTypedText("")}
          className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition shadow-md"
        >
          Clear Text
        </button>
      </div>

      {/* Webcam + Keyboard (only if preview is on) */}
      {camOn && (
        <div
          ref={wrapperRef}
          className="relative mt-6 w-full max-w-[1024px] bg-gray-50 rounded-2xl shadow-2xl p-4"
          style={{ minHeight: 650 }}
        >
          <Webcam
            ref={webcamRef}
            audio={false}
            mirrored={true}
            screenshotFormat="image/jpeg"
            videoConstraints={{ width: 1000, height: 600 }}
            className="w-full rounded-xl object-cover border border-gray-300 shadow-md"
          />

          <div
            id="hk-finger-dot"
            style={{
              position: "absolute", width: 14, height: 14, borderRadius: 14,
              background: "rgba(255,50,50,0.9)", transform: "translate(-50%, -50%)",
              pointerEvents: "none", left: 0, top: 0, opacity: 0,
              transition: "opacity 0.08s linear", zIndex: 9999,
            }}
          />

          {/* Keyboard Overlay */}
          <div
            id="keyboard"
            className="absolute left-1/2 transform -translate-x-1/2 bottom-6 pointer-events-auto"
          >
            <div className="flex flex-col gap-2 p-6 bg-white rounded-3xl shadow-2xl border border-gray-300 w-[1000px] mx-auto">
              {rows.map((row, rowIndex) => (
                <div key={rowIndex} className="flex justify-between gap-1 w-full">
                  {row.map((key, i) => {
                    const uid = `${key.label}__r${rowIndex}__c${i}`;
                    const isActive = activeUid === uid;
                    const depth = depthColors[key.type] || depthColors.default;
                    const baseColor = keyColors[key.type] || keyColors.default;
                    const activeColor = activeColors[key.type] || activeColors.default;
                    const shape = rowIndex === 0 ? "rounded-md h-8 w-10" : "rounded-xl h-12 " + (key.wide ? key.wide : "w-12");

                    return (
                      <button
                        key={i}
                        ref={(el) => { buttonRefs.current[uid] = { el, label: key.label }; }}
                        onMouseDown={() => handlePress(uid, key.label)}
                        onMouseUp={() => setActiveUid(null)}
                        onMouseLeave={() => setActiveUid(null)}
                        className={`relative ${shape} text-sm font-semibold flex justify-center items-center bg-gradient-to-b ${isActive ? activeColor : baseColor} transition-all duration-150 border border-gray-300`}
                        style={{ boxShadow: isActive ? `inset 2px 2px 6px rgba(0,0,0,0.5)` : `0 4px 0 ${depth}, 0 6px 10px rgba(0,0,0,0.25)`, transform: isActive ? "translateY(2px) scale(0.98)" : "none" }}
                      >
                        <KeyLabel label={key.label} />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
